import { useEffect, useRef } from "react"
import gsap from "gsap"
import { cn } from "@/lib/utils"

/**
 * Configuración declarativa de cada "gota" de líquido: posición base, tamaño,
 * color (tintes azules del tema) y el desplazamiento que anima GSAP.
 * Determinista a propósito (sin Math.random) para un render SSG estable; la
 * *velocidad* de cada gota sí se aleatoriza, pero en cliente (ver `useEffect`).
 */
const BLOBS = [
	// Manchas grandes y suaves en azules claros (más tenues para no tapar el texto).
	{ className: "left-[6%] top-[14%] size-72 bg-sky-200/60", x: 80, y: 60 },
	{ className: "left-[50%] top-[8%] size-80 bg-chart-1/45", x: -70, y: 90 },
	{ className: "left-[26%] top-[42%] size-96 bg-sky-100/70", x: 60, y: -50 },
	{ className: "left-[66%] top-[46%] size-72 bg-primary/20", x: -90, y: -40 },
	{ className: "left-[12%] top-[58%] size-64 bg-chart-2/35", x: 70, y: 70 },
	// Gotas pequeñas que aportan textura de agua.
	{ className: "left-[20%] top-[24%] size-16 bg-primary/25", x: 50, y: -40 },
	{ className: "left-[40%] top-[60%] size-12 bg-sky-300/50", x: -45, y: 55 },
	{ className: "left-[74%] top-[20%] size-20 bg-chart-2/40", x: 40, y: 60 },
	{ className: "left-[58%] top-[66%] size-10 bg-primary/25", x: -50, y: -45 },
	{ className: "left-[86%] top-[58%] size-14 bg-sky-300/45", x: -40, y: -50 },
	{ className: "left-[34%] top-[16%] size-8 bg-primary/30", x: 35, y: 45 },
	{ className: "left-[90%] top-[32%] size-12 bg-chart-1/50", x: -30, y: 50 }
]

// Rango de duración (s) de un ciclo de vaivén: cada gota toma un valor aleatorio
// dentro de él, así ninguna va sincronizada. Valores bajos = más rápido; se
// mantiene por encima de ~4 s para conservar el vaivén lento y fluido del agua.
const DURATION_MIN = 4
const DURATION_MAX = 7.5

/**
 * Fondo animado de "agua en movimiento": gotas azules que se desplazan con GSAP
 * y se funden mediante un filtro SVG tipo *goo* (metaballs) más un desenfoque
 * final, dando un aspecto líquido y limpio sobre fondo claro.
 *
 * Reutilizable como telón de fondo de cualquier sección (hero, CTA, etc.).
 * Respeta `prefers-reduced-motion`: si está activo, las gotas quedan estáticas.
 */
export function LiquidBackground({ className }: { className?: string }) {
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const root = containerRef.current
		if (!root) return
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

		const ctx = gsap.context(() => {
			const blobs = root.querySelectorAll<HTMLElement>(".liquid-blob")
			BLOBS.forEach((cfg, i) => {
				const el = blobs[i]
				if (!el) return
				gsap.to(el, {
					x: cfg.x,
					y: cfg.y,
					// Velocidad aleatoria e independiente por gota (más rápida que
					// antes pero aún lenta, para simular el movimiento del líquido).
					duration: gsap.utils.random(DURATION_MIN, DURATION_MAX),
					// Pequeño desfase inicial aleatorio para desincronizarlas.
					delay: gsap.utils.random(0, 2),
					repeat: -1,
					yoyo: true,
					ease: "sine.inOut"
				})
			})
		}, root)

		return () => ctx.revert()
	}, [])

	return (
		<div ref={containerRef} aria-hidden className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}>
			<svg className="absolute size-0">
				<defs>
					<filter id="liquid-goo">
						<feGaussianBlur in="SourceGraphic" stdDeviation="14" result="blur" />
						<feColorMatrix
							in="blur"
							mode="matrix"
							values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
							result="goo"
						/>
						<feBlend in="SourceGraphic" in2="goo" />
					</filter>
				</defs>
			</svg>

			<div className="absolute inset-0 filter-[url(#liquid-goo)_blur(6px)]">
				{BLOBS.map((b, i) => (
					<div key={i} className={cn("liquid-blob absolute rounded-full", b.className)} />
				))}
			</div>
		</div>
	)
}
